import { PDFDocument, rgb } from "pdf-lib";
import type { PDFPage, PDFFont } from "pdf-lib";
import { embedEUkraineFonts, truncateToWidth } from "./pdfHelpers";
import type { InsightsResult } from "../../shared/src/protocol";

const PAGE_W = 595.276; // A4
const PAGE_H = 841.89;
const MARGIN = 36;
const CONTENT_W = PAGE_W - MARGIN * 2;

const TITLE_SIZE = 16;
const HEADING_SIZE = 11;
const BODY_SIZE = 9;
const SMALL_SIZE = 7.5;

const COLOR_TEXT = rgb(0.1, 0.1, 0.1);
const COLOR_MUTED = rgb(0.45, 0.45, 0.45);
const COLOR_BORDER = rgb(0.85, 0.85, 0.85);
const COLOR_HEADER_BG = rgb(0.95, 0.95, 0.95);

interface ExportOpts {
  data: InsightsResult;
  charts: Array<{ title: string; png: string | null }>;
  filters: Record<string, string>;
  labels: {
    title: string;
    generated: string;
    filters: string;
    totalCases: string;
    totalDeaths: string;
    byRegion: string;
    byHospital: string;
    byUnit: string;
    byTrauma: string;
    cases: string;
    deaths: string;
    brand: string;
    brandTagline: string;
  };
  anon: {
    anonHospital: (id: string, name: string) => string;
    anonUnit: (id: string, name: string) => string;
    anonRegion: (id: string, name: string) => string;
  };
}

export async function exportInsightsPdf(opts: ExportOpts): Promise<Uint8Array> {
  const { data, charts, filters, labels, anon } = opts;
  const pdf = await PDFDocument.create();
  const { regular: font, bold: fontBold } = await embedEUkraineFonts(pdf);

  let logo: Awaited<ReturnType<typeof pdf.embedPng>> | null = null;
  try {
    const logoBytes = await fetch("/icons/icon-192.png").then((r) =>
      r.arrayBuffer(),
    );
    logo = await pdf.embedPng(new Uint8Array(logoBytes));
  } catch {
    /* logo missing → render without */
  }

  const ctx: PageCtx = {
    pdf,
    font,
    fontBold,
    page: pdf.addPage([PAGE_W, PAGE_H]),
    cursorY: PAGE_H - MARGIN,
  };

  drawHeader(ctx, labels, logo);
  ctx.cursorY -= 8;

  // Filters
  drawText(ctx, labels.filters, fontBold, HEADING_SIZE, COLOR_TEXT);
  for (const [k, v] of Object.entries(filters)) {
    drawText(ctx, `${k}: ${v}`, font, BODY_SIZE, COLOR_TEXT);
  }
  ctx.cursorY -= 8;

  // Summary
  drawText(
    ctx,
    `${labels.totalCases}: ${data.totalCases}    ${labels.totalDeaths}: ${data.totalDeaths}`,
    fontBold,
    HEADING_SIZE,
    COLOR_TEXT,
  );
  ctx.cursorY -= 8;

  // Charts
  for (const chart of charts) {
    if (!chart.png) continue;
    await drawChart(ctx, chart.title, chart.png);
  }

  // Tables
  if (data.byRegion.length > 1) {
    drawTable(
      ctx,
      labels.byRegion,
      [labels.cases, labels.deaths],
      data.byRegion.map((r) => ({
        label: r.id === "__none__" ? "—" : anon.anonRegion(r.id, r.name) || r.id.slice(-6),
        cases: r.cases,
        deaths: r.deaths,
      })),
    );
  }
  if (data.byHospital.length > 1) {
    drawTable(
      ctx,
      labels.byHospital,
      [labels.cases, labels.deaths],
      data.byHospital.map((r) => ({
        label: anon.anonHospital(r.id, r.name) || r.id.slice(-6),
        cases: r.cases,
        deaths: r.deaths,
      })),
    );
  }
  if (data.byUnit.length > 1) {
    drawTable(
      ctx,
      labels.byUnit,
      [labels.cases, labels.deaths],
      data.byUnit.map((r) => ({
        label: anon.anonUnit(r.id, r.name) || r.id.slice(-6),
        cases: r.cases,
        deaths: r.deaths,
      })),
    );
  }
  if (data.byTrauma.length > 0) {
    drawTable(
      ctx,
      labels.byTrauma,
      [labels.cases, labels.deaths],
      data.byTrauma.map((r) => ({
        label: r.type,
        cases: r.cases,
        deaths: r.deaths,
      })),
    );
  }

  const pages = pdf.getPages();
  const footer = labels.brand;
  for (let i = 0; i < pages.length; i++) {
    const p = pages[i];
    const pageNum = `${i + 1} / ${pages.length}`;
    p.drawText(footer, {
      x: MARGIN,
      y: 18,
      size: SMALL_SIZE,
      font,
      color: COLOR_MUTED,
    });
    const pnW = font.widthOfTextAtSize(pageNum, SMALL_SIZE);
    p.drawText(pageNum, {
      x: PAGE_W - MARGIN - pnW,
      y: 18,
      size: SMALL_SIZE,
      font,
      color: COLOR_MUTED,
    });
  }

  return pdf.save();
}

interface PageCtx {
  pdf: PDFDocument;
  font: PDFFont;
  fontBold: PDFFont;
  page: PDFPage;
  cursorY: number;
}

function ensureSpace(ctx: PageCtx, needed: number) {
  if (ctx.cursorY - needed < MARGIN) {
    ctx.page = ctx.pdf.addPage([PAGE_W, PAGE_H]);
    ctx.cursorY = PAGE_H - MARGIN;
  }
}

function drawText(
  ctx: PageCtx,
  text: string,
  font: PDFFont,
  size: number,
  color: ReturnType<typeof rgb>,
) {
  ensureSpace(ctx, size + 2);
  ctx.cursorY -= size;
  ctx.page.drawText(text, {
    x: MARGIN,
    y: ctx.cursorY,
    size,
    font,
    color,
  });
  ctx.cursorY -= 2;
}

async function drawChart(ctx: PageCtx, title: string, pngDataUrl: string) {
  const base64 = pngDataUrl.split(",")[1];
  if (!base64) return;
  const img = await ctx.pdf.embedPng(
    Uint8Array.from(atob(base64), (c) => c.charCodeAt(0)),
  );
  const maxW = CONTENT_W;
  const maxH = 220;
  const scale = Math.min(maxW / img.width, maxH / img.height);
  const w = img.width * scale;
  const h = img.height * scale;
  ensureSpace(ctx, h + 24);
  drawText(ctx, title, ctx.fontBold, HEADING_SIZE, COLOR_TEXT);
  ctx.cursorY -= h;
  ctx.page.drawImage(img, {
    x: MARGIN + (CONTENT_W - w) / 2,
    y: ctx.cursorY,
    width: w,
    height: h,
  });
  ctx.cursorY -= 12;
}

function drawTable(
  ctx: PageCtx,
  title: string,
  cols: [string, string],
  rows: Array<{ label: string; cases: number; deaths: number }>,
) {
  ensureSpace(ctx, 40);
  drawText(ctx, title, ctx.fontBold, HEADING_SIZE, COLOR_TEXT);

  const labelW = CONTENT_W - 140;
  const colW = 70;
  const headerH = 14;
  const rowH = 12;

  // Header
  ensureSpace(ctx, headerH);
  ctx.cursorY -= headerH;
  ctx.page.drawRectangle({
    x: MARGIN,
    y: ctx.cursorY,
    width: CONTENT_W,
    height: headerH,
    color: COLOR_HEADER_BG,
  });
  ctx.page.drawText("", {
    x: MARGIN + 4,
    y: ctx.cursorY + 4,
    size: BODY_SIZE,
    font: ctx.fontBold,
  });
  ctx.page.drawText(cols[0], {
    x: MARGIN + labelW + colW - measureText(ctx.fontBold, cols[0], BODY_SIZE) - 4,
    y: ctx.cursorY + 4,
    size: BODY_SIZE,
    font: ctx.fontBold,
    color: COLOR_TEXT,
  });
  ctx.page.drawText(cols[1], {
    x: MARGIN + labelW + colW * 2 - measureText(ctx.fontBold, cols[1], BODY_SIZE) - 4,
    y: ctx.cursorY + 4,
    size: BODY_SIZE,
    font: ctx.fontBold,
    color: COLOR_TEXT,
  });

  // Rows
  let totalCases = 0;
  let totalDeaths = 0;
  for (const row of rows) {
    ensureSpace(ctx, rowH);
    ctx.cursorY -= rowH;
    const label = truncateToWidth(ctx.font, row.label, BODY_SIZE, labelW - 8);
    ctx.page.drawText(label, {
      x: MARGIN + 4,
      y: ctx.cursorY + 3,
      size: BODY_SIZE,
      font: ctx.font,
      color: COLOR_TEXT,
    });
    const cs = String(row.cases);
    const ds = String(row.deaths);
    ctx.page.drawText(cs, {
      x: MARGIN + labelW + colW - measureText(ctx.font, cs, BODY_SIZE) - 4,
      y: ctx.cursorY + 3,
      size: BODY_SIZE,
      font: ctx.font,
      color: COLOR_TEXT,
    });
    ctx.page.drawText(ds, {
      x: MARGIN + labelW + colW * 2 - measureText(ctx.font, ds, BODY_SIZE) - 4,
      y: ctx.cursorY + 3,
      size: BODY_SIZE,
      font: ctx.font,
      color: COLOR_TEXT,
    });
    ctx.page.drawLine({
      start: { x: MARGIN, y: ctx.cursorY },
      end: { x: MARGIN + CONTENT_W, y: ctx.cursorY },
      thickness: 0.3,
      color: COLOR_BORDER,
    });
    totalCases += row.cases;
    totalDeaths += row.deaths;
  }

  // Totals row
  ensureSpace(ctx, rowH);
  ctx.cursorY -= rowH;
  ctx.page.drawRectangle({
    x: MARGIN,
    y: ctx.cursorY,
    width: CONTENT_W,
    height: rowH,
    color: COLOR_HEADER_BG,
  });
  ctx.page.drawText("Σ", {
    x: MARGIN + 4,
    y: ctx.cursorY + 3,
    size: BODY_SIZE,
    font: ctx.fontBold,
    color: COLOR_TEXT,
  });
  const tcs = String(totalCases);
  const tds = String(totalDeaths);
  ctx.page.drawText(tcs, {
    x: MARGIN + labelW + colW - measureText(ctx.fontBold, tcs, BODY_SIZE) - 4,
    y: ctx.cursorY + 3,
    size: BODY_SIZE,
    font: ctx.fontBold,
    color: COLOR_TEXT,
  });
  ctx.page.drawText(tds, {
    x: MARGIN + labelW + colW * 2 - measureText(ctx.fontBold, tds, BODY_SIZE) - 4,
    y: ctx.cursorY + 3,
    size: BODY_SIZE,
    font: ctx.fontBold,
    color: COLOR_TEXT,
  });
  ctx.cursorY -= 8;
}

function measureText(font: PDFFont, text: string, size: number): number {
  return font.widthOfTextAtSize(text, size);
}

function drawHeader(
  ctx: PageCtx,
  labels: ExportOpts["labels"],
  logo: Awaited<ReturnType<PDFDocument["embedPng"]>> | null,
) {
  const headerH = 44;
  ensureSpace(ctx, headerH);
  ctx.cursorY -= headerH;
  const top = ctx.cursorY + headerH;

  if (logo) {
    const size = 32;
    ctx.page.drawImage(logo, {
      x: MARGIN,
      y: top - size - 4,
      width: size,
      height: size,
    });
  }

  const textX = MARGIN + (logo ? 40 : 0);
  ctx.page.drawText(labels.brand, {
    x: textX,
    y: top - 16,
    size: TITLE_SIZE,
    font: ctx.fontBold,
    color: COLOR_TEXT,
  });
  ctx.page.drawText(labels.brandTagline, {
    x: textX,
    y: top - 28,
    size: SMALL_SIZE,
    font: ctx.font,
    color: COLOR_MUTED,
  });

  const titleText = labels.title;
  const titleW = ctx.fontBold.widthOfTextAtSize(titleText, HEADING_SIZE);
  ctx.page.drawText(titleText, {
    x: PAGE_W - MARGIN - titleW,
    y: top - 16,
    size: HEADING_SIZE,
    font: ctx.fontBold,
    color: COLOR_TEXT,
  });
  const stamp = `${labels.generated}: ${new Date().toLocaleString()}`;
  const stampW = ctx.font.widthOfTextAtSize(stamp, SMALL_SIZE);
  ctx.page.drawText(stamp, {
    x: PAGE_W - MARGIN - stampW,
    y: top - 28,
    size: SMALL_SIZE,
    font: ctx.font,
    color: COLOR_MUTED,
  });

  ctx.page.drawLine({
    start: { x: MARGIN, y: top - 38 },
    end: { x: PAGE_W - MARGIN, y: top - 38 },
    thickness: 0.5,
    color: COLOR_BORDER,
  });
}
